/* External */
import {
  memo, useCallback, useEffect, useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';

/* Components */
import MyListContainer from './MyListContainer';
import CatalogVodSkeleton from '../../../components/CatalogVodSkeleton';
import LanderLayout from '../../../components/LanderLayout';

/* Providers */
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Utils */
import useFetchCarousel from '../../../hooks/useFetchCarousel';
import useCarouselContents from '../../../hooks/useCarouselContents';
import { MY_LIST_PARAMS } from '../../../utils/constants';
import { useOnBackPress, useOnKeyDown } from '../../../utils/keyDownEvents';
import { Direction } from '../../../utils/spatialNavigation';
import { useOpenSidebar } from '../../../providers/SidebarStateContext';
import platform from '../../../utils/platform';
import { useCountry } from '../../../providers/ConfigProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';

/* Styles */
import './MyList.scss';

const CARDS_PER_ROW = 5;
const LEFT_KEY_CODES = [platform.keyCodes.navigation.left];

function MyList() {
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const country = useCountry();
  const goToSidebar = useOpenSidebar();
  const { carousel, isFetching } = useFetchCarousel({
    sourceCarouselId: MY_LIST_PARAMS.SOURCE_CAROUSEL_ID,
    type: MY_LIST_PARAMS.TYPE,
    source: MY_LIST_PARAMS.SOURCE,
  });

  const items = useCarouselContents(carousel);

  const handleBackPress = useCallback(() => {
    goToSidebar();
  }, [goToSidebar]);

  const isMyListEmpty = items.size === 0 || items.length === 0;

  useOnBackPress({ listener: handleBackPress });

  useOnKeyDown({
    listener: handleBackPress,
    forKeyCodes: LEFT_KEY_CODES,
    when: isMyListEmpty,
  });

  const handleLeftArrowPress = (direction, { index }) => {
    const isInLeftColumn = index % CARDS_PER_ROW === 0;

    if (direction === Direction.LEFT && isInLeftColumn) {
      goToSidebar();
    }
  };

  const myListCommonTagEvent = useCallback(({ eventName = '', data = {} }) => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.MY_LIST_DATA,
      subCategory: GTM_SUBCATEGORIES.MY_LIST.LANDER,
      eventName,
      data,
    });
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      myListCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [myListCommonTagEvent, country]);

  return (
    <LanderLayout className="dtv-html5-c-my-list">
      <h1 className="dtv-html5-c-my-list__title">
        <FormattedMessage id="myList.title" />
      </h1>
      {isFetching
        ? <CatalogVodSkeleton className="dtv-html5-c-my-list__catalog-skeleton" numberOfCards={10} />
        : (
          <MyListContainer
            isMyListEmpty={isMyListEmpty}
            cardsPerRow={CARDS_PER_ROW}
            items={items}
            onCardArrowPress={handleLeftArrowPress}
            myListCommonTagEvent={myListCommonTagEvent}
          />
        ) }
    </LanderLayout>
  );
}

export default memo(MyList);
